import React from 'react'

import styled from '@emotion/styled'

import { Colors, getColor } from '../theme/colors'
import Card from './Card'
import Stack from './Stack'

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  > :first-child {
    flex: 1;
  }
`

const Title = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${props => props.theme.colors.secondaryLabel};
  ${props => props.theme.typography.smallLabel};
`

const Value = styled.span`
  color: ${props => props.theme.colors.label};
  ${props => props.theme.typography.mainTitle};
`

const Label = styled.span<{ labelColor?: Colors }>`
  color: ${({ labelColor, theme }) => (labelColor ? getColor(labelColor) : theme.colors.secondaryLabel)};
  ${props => props.theme.typography.caption};
`

const Details = styled.dl`
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: 1fr 1fr;
`

const DetailValue = styled.dt<{ valueColor?: Colors }>`
  color: ${({ valueColor, theme }) => (valueColor ? getColor(valueColor) : theme.colors.label)};
  ${props => props.theme.typography.caption};
`

const DetailLabel = styled.dd`
  color: ${props => props.theme.colors.tertiaryLabel};
  ${props => props.theme.typography.caption};
`

export type SummaryCardProps = {
  title?: string
  label?: string
  labelColor?: Colors
  value: React.ReactNode
  action?: React.ReactNode
  details?: Array<{ value: string | number; valueColor?: Colors; label?: string }>
}

const SummaryCard = ({ title, label, labelColor, value, action, details, ...props }: SummaryCardProps) => {
  return (
    <Card {...props}>
      <Stack space="1.5rem">
        <Header>
          <Stack space="0.25rem">
            {title && <Title>{title}</Title>}
            <Value data-testid="summary-card-value">{value}</Value>
            {label && <Label labelColor={labelColor}>{label}</Label>}
          </Stack>
          {action}
        </Header>
        {details && details.length > 0 && (
          <Details>
            {details.map(detail => (
              <div key={detail.label ?? detail.value}>
                {detail.label && <DetailLabel>{detail.label}</DetailLabel>}
                {detail.value && <DetailValue valueColor={detail.valueColor}>{detail.value}</DetailValue>}
              </div>
            ))}
          </Details>
        )}
      </Stack>
    </Card>
  )
}

export default SummaryCard
